
import { supabase } from '@/integrations/supabase/client';
import { 
  AI_PRAYER_ASSISTANT_SETTINGS_KEY,
  FORM_SETTINGS_KEY,
  FORM_SETTINGS_UPDATED
} from '@/constants/storage.constants';

export interface LegacyAiPrayerSettings {
  title?: string;
  logo?: string;
  avatar?: string;
  isEnabled?: boolean;
  isMinimized?: boolean;
  themeSettings?: any;
}

export interface LegacyFormSettings {
  availableTags?: string[];
  enableSubject?: boolean;
  enableScripture?: boolean;
  maxRequestLength?: number;
  defaultPrompt?: string;
}

/**
 * Migrate AI Prayer Assistant settings from localStorage to Supabase
 */
export async function migrateAiPrayerAssistantSettings(userId: string): Promise<boolean> {
  try {
    // Check if user already has settings in Supabase
    const { data: existingSettings } = await supabase
      .from('ai_prayer_assistant_settings')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (existingSettings) {
      console.log('User already has AI Prayer Assistant settings in Supabase, skipping migration');
      return true;
    }

    // Try to load legacy settings from localStorage
    const legacySettings = loadLegacyAiPrayerSettings();
    
    if (!legacySettings || Object.keys(legacySettings).length === 0) {
      console.log('No legacy AI Prayer Assistant settings found');
      return true;
    }

    // Insert migrated settings into Supabase
    const { error } = await supabase
      .from('ai_prayer_assistant_settings')
      .insert({
        user_id: userId,
        title: legacySettings.title || 'Let Me Pray For You',
        logo_url: legacySettings.logo || null,
        avatar_url: legacySettings.avatar || null,
        is_enabled: legacySettings.isEnabled !== false, // Default to true
        is_minimized: legacySettings.isMinimized || false,
        theme_settings: legacySettings.themeSettings || {}
      });

    if (error) {
      console.error('Error migrating AI Prayer Assistant settings:', error);
      return false;
    }

    console.log('Successfully migrated AI Prayer Assistant settings to Supabase');
    
    // Optionally clean up localStorage after successful migration
    // localStorage.removeItem(AI_PRAYER_ASSISTANT_SETTINGS_KEY);
    
    return true;
  } catch (error) {
    console.error('Migration error:', error);
    return false;
  }
}

/**
 * Migrate prayer requests from localStorage to Supabase
 */
export async function migratePrayerRequests(userId: string): Promise<boolean> {
  try {
    // Check for any existing prayer request patterns in localStorage
    const legacyRequests = loadLegacyPrayerRequests();
    
    if (!legacyRequests || legacyRequests.length === 0) {
      console.log('No legacy prayer requests found');
      return true;
    }

    // Insert migrated requests into Supabase
    const { error } = await supabase
      .from('ai_prayer_requests')
      .insert(
        legacyRequests.map(request => ({
          user_id: userId,
          request_text: request.text || '',
          subject: request.subject || null,
          selected_tags: request.tags || [],
          include_bible_verse: request.includeBibleVerse || false,
          ai_response: request.response || null,
          status: request.response ? 'completed' : 'pending'
        }))
      );

    if (error) {
      console.error('Error migrating prayer requests:', error);
      return false;
    }

    console.log(`Successfully migrated ${legacyRequests.length} prayer requests to Supabase`);
    return true;
  } catch (error) {
    console.error('Prayer requests migration error:', error);
    return false;
  }
}

/**
 * Load legacy AI Prayer Assistant settings from localStorage
 */
function loadLegacyAiPrayerSettings(): LegacyAiPrayerSettings | null {
  try {
    const saved = localStorage.getItem(AI_PRAYER_ASSISTANT_SETTINGS_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
    return null;
  } catch (error) {
    console.error('Error loading legacy AI Prayer settings:', error);
    return null;
  }
}

/**
 * Load legacy form settings from localStorage
 */
function loadLegacyFormSettings(): LegacyFormSettings | null {
  try {
    const saved = localStorage.getItem(FORM_SETTINGS_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
    return null;
  } catch (error) {
    console.error('Error loading legacy form settings:', error);
    return null;
  }
}

/**
 * Load legacy prayer requests from localStorage (if any pattern exists)
 */
function loadLegacyPrayerRequests(): any[] {
  try {
    // Check for various localStorage keys that might contain prayer data
    const possibleKeys = [
      'ai-prayer-requests',
      'prayer-assistant-history',
      'generated-prayers',
      'prayer-history'
    ];
    
    const allRequests: any[] = [];
    
    for (const key of possibleKeys) {
      const data = localStorage.getItem(key);
      if (data) {
        try {
          const parsed = JSON.parse(data);
          if (Array.isArray(parsed)) {
            allRequests.push(...parsed);
          } else if (parsed && typeof parsed === 'object') {
            allRequests.push(parsed);
          }
        } catch (parseError) {
          console.warn(`Could not parse localStorage item ${key}:`, parseError);
        }
      }
    }
    
    return allRequests;
  } catch (error) {
    console.error('Error loading legacy prayer requests:', error);
    return [];
  }
}

/**
 * Get fallback settings when Supabase data is not available
 */
export function getFallbackAiPrayerSettings(): LegacyAiPrayerSettings {
  const legacySettings = loadLegacyAiPrayerSettings();
  
  return {
    title: legacySettings?.title || 'Let Me Pray For You',
    logo: legacySettings?.logo || null,
    avatar: legacySettings?.avatar || null,
    isEnabled: legacySettings?.isEnabled !== false,
    isMinimized: legacySettings?.isMinimized || false,
    themeSettings: legacySettings?.themeSettings || {}
  };
}

/**
 * Get fallback form settings when Supabase data is not available
 */
export function getFallbackFormSettings(): LegacyFormSettings {
  const legacySettings = loadLegacyFormSettings();
  
  return {
    availableTags: legacySettings?.availableTags || [
      'Healing', 'Guidance', 'Protection', 'Thanksgiving', 
      'Wisdom', 'Forgiveness', 'Strength', 'Peace'
    ],
    enableSubject: legacySettings?.enableSubject !== false,
    enableScripture: legacySettings?.enableScripture !== false,
    maxRequestLength: legacySettings?.maxRequestLength || 300,
    defaultPrompt: legacySettings?.defaultPrompt || "What has God brought you here to share today?"
  };
}

/**
 * Run complete migration for a user
 */
export async function runCompleteAiPrayerMigration(userId: string): Promise<boolean> {
  console.log('Starting AI Prayer Assistant migration for user:', userId);
  
  try {
    const settingsMigrated = await migrateAiPrayerAssistantSettings(userId);
    const requestsMigrated = await migratePrayerRequests(userId);
    
    const success = settingsMigrated && requestsMigrated;
    
    if (success) {
      console.log('AI Prayer Assistant migration completed successfully');
    } else {
      console.warn('AI Prayer Assistant migration completed with some issues');
    }
    
    return success;
  } catch (error) {
    console.error('Complete migration failed:', error);
    return false;
  }
}

/**
 * Check if user needs migration
 */
export async function checkMigrationNeeded(userId: string): Promise<boolean> {
  try {
    const { data: existingSettings } = await supabase
      .from('ai_prayer_assistant_settings')
      .select('id')
      .eq('user_id', userId)
      .single();
    
    // If user has no settings in Supabase but has localStorage data, migration is needed
    if (!existingSettings) {
      const legacySettings = loadLegacyAiPrayerSettings();
      return legacySettings !== null && Object.keys(legacySettings).length > 0;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking migration status:', error);
    return false;
  }
}
